import Layout from "../components/Layout";
import SEO from "../components/SEO";
import { getAllPosts, PostMeta } from "../services/postService";
import { Link } from "react-router-dom";

const formatListingDate = (dateString: string) => {
  const [year, month, day] = dateString.split("-").map(Number);
  const localDate = new Date(year, month - 1, day);
  return localDate
    .toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    .toUpperCase();
};

interface CardProps {
  post: PostMeta;
}

const StackedCard = ({ post }: CardProps) => (
  <Link to={`/blog/${post.slug}`} className="block group">
    {post.coverImage && (
      <div className="overflow-hidden rounded-md bg-[#000000]/5 aspect-[4/3] mb-3">
        <img
          src={post.coverImage}
          alt={post.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          loading="lazy"
        />
      </div>
    )}
    <h2 className="text-lg sm:text-xl font-bold tracking-tight leading-snug text-[#000000] line-clamp-2 transition-colors duration-200 group-hover:text-[#000000]/60">
      {post.title}
    </h2>
    {post.excerpt && (
      <p className="mt-2 text-sm sm:text-base text-[#000000]/65 leading-snug line-clamp-3 transition-colors duration-200 group-hover:text-[#000000]/45">
        {post.excerpt}
      </p>
    )}
    <p className="mt-3 text-[10px] sm:text-xs tracking-wide text-[#000000]/55 transition-colors duration-200 group-hover:text-[#000000]/40">
      {formatListingDate(post.date)} · BEKAH
    </p>
  </Link>
);

const HorizontalCard = ({ post }: CardProps) => (
  <Link to={`/blog/${post.slug}`} className="group flex gap-3 sm:gap-4">
    {post.coverImage && (
      <div className="shrink-0 self-stretch w-20 sm:w-24 aspect-square overflow-hidden rounded-md bg-[#000000]/5">
        <img
          src={post.coverImage}
          alt={post.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          loading="lazy"
        />
      </div>
    )}
    <div className="flex-1 min-w-0 flex flex-col">
      <h2 className="text-base sm:text-lg font-bold tracking-tight leading-snug text-[#000000] line-clamp-2 transition-colors duration-200 group-hover:text-[#000000]/60">
        {post.title}
      </h2>
      {post.excerpt && (
        <p className="mt-1.5 text-xs sm:text-sm text-[#000000]/65 leading-snug line-clamp-2 transition-colors duration-200 group-hover:text-[#000000]/45">
          {post.excerpt}
        </p>
      )}
      <p className="mt-2 text-[10px] sm:text-xs tracking-wide text-[#000000]/55 transition-colors duration-200 group-hover:text-[#000000]/40">
        {formatListingDate(post.date)} · BEKAH
      </p>
    </div>
  </Link>
);

const Essays = () => {
  const posts = getAllPosts();

  return (
    <Layout>
      <SEO
        title="Essays"
        description="Reflections by Bekah June on technology, founder life, productivity, and the human side of building things."
        type="website"
        url="/essays"
        image="/desk.jpg"
        imageAlt="Essays by Bekah June"
        keywords={["tech essays", "startup blog", "productivity tips", "developer blog", "founder journey"]}
      />
      <section className="px-4 sm:px-6 py-6 sm:py-10">
        <div className="w-full max-w-5xl mx-auto">
          {posts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-base sm:text-lg text-[#000000]/80">No posts found. Check back soon!</p>
            </div>
          ) : (
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-x-5 lg:gap-x-7">
              {posts.map((post, index) => {
                const isStacked = index % 3 === 0;
                return (
                  <article
                    key={post.slug}
                    className="break-inside-avoid mb-6 lg:mb-8"
                  >
                    {isStacked ? (
                      <StackedCard post={post} />
                    ) : (
                      <HorizontalCard post={post} />
                    )}
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default Essays;
